/**
 * OnboardingPage - Multi-step onboarding survey (4-5 steps).
 *
 * Split-screen layout matching SignupPage.
 * Collects strategic data from new users via selectable cards.
 * For flow=trial: adds a 5th step with credit card registration.
 * Saves all answers to backend on finalize, then redirects to dashboard.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@/contexts/LanguageContext';
import { useUserStatus } from '@/contexts/UserStatusContext';
import { apiPost } from '@/services/apiClient';
import CoreStudioLogo from '@/components/ui/core-studio-logo';
import CoreStudioMark from '@/components/ui/core-studio-mark';
import SelectableCard from '@/components/SelectableCard';
import TrialCardStep from '@/components/TrialCardStep';
import type { TrialCardFormData } from '@/components/TrialCardStep';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { blockInSupportMode } from '@/lib/impersonation';

// ============================================================================
// STEP DEFINITIONS
// ============================================================================

interface StepOption {
  value: string;
  labelKey: string;
}

interface StepDefinition {
  questionKey: string;
  answerField: string;
  options: StepOption[];
  columns: 1 | 2;
}

const STEPS: StepDefinition[] = [
  {
    questionKey: 'onboardingSurvey.step1.question',
    answerField: 'objetivo_carrosseis',
    options: [
      { value: 'crescer_audiencia', labelKey: 'onboardingSurvey.step1.opt1' },
      { value: 'gerar_leads', labelKey: 'onboardingSurvey.step1.opt2' },
      { value: 'vender_produtos', labelKey: 'onboardingSurvey.step1.opt3' },
      { value: 'posicionar_autoridade', labelKey: 'onboardingSurvey.step1.opt4' },
      { value: 'testando', labelKey: 'onboardingSurvey.step1.opt5' },
    ],
    columns: 2,
  },
  {
    questionKey: 'onboardingSurvey.step2.question',
    answerField: 'tipo_conteudo',
    options: [
      { value: 'reels_e_carrosseis', labelKey: 'onboardingSurvey.step2.opt1' },
      { value: 'reels_raramente_carrosseis', labelKey: 'onboardingSurvey.step2.opt2' },
      { value: 'apenas_carrosseis', labelKey: 'onboardingSurvey.step2.opt3' },
      { value: 'nao_produz', labelKey: 'onboardingSurvey.step2.opt4' },
    ],
    columns: 1,
  },
  {
    questionKey: 'onboardingSurvey.step3.question',
    answerField: 'leads_instagram',
    options: [
      { value: 'sim_frequentemente', labelKey: 'onboardingSurvey.step3.opt1' },
      { value: 'as_vezes', labelKey: 'onboardingSurvey.step3.opt2' },
      { value: 'raramente', labelKey: 'onboardingSurvey.step3.opt3' },
    ],
    columns: 1,
  },
  {
    questionKey: 'onboardingSurvey.step4.question',
    answerField: 'faturamento',
    options: [
      { value: 'nao_faturo', labelKey: 'onboardingSurvey.step4.opt1' },
      { value: 'ate_10k', labelKey: 'onboardingSurvey.step4.opt2' },
      { value: '10k_50k', labelKey: 'onboardingSurvey.step4.opt3' },
      { value: '50k_200k', labelKey: 'onboardingSurvey.step4.opt4' },
      { value: 'acima_200k', labelKey: 'onboardingSurvey.step4.opt5' },
    ],
    columns: 2,
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { refreshStatus } = useUserStatus();
  // Read flow from URL params first, fallback to sessionStorage (survives redirect cycles)
  const flow = searchParams.get('flow') || (() => {
    try { return sessionStorage.getItem('cai_onboarding_flow') || ''; } catch { return ''; }
  })();

  // Trial flow adds a card registration step after the survey
  const isTrialFlow = flow === 'trial';
  const totalSteps = STEPS.length + (isTrialFlow ? 1 : 0);
  const cardStepIndex = STEPS.length; // index 4 (5th step)

  const [currentStep, setCurrentStep] = useState(() => {
    try {
      const saved = sessionStorage.getItem('cai_onboarding_step');
      return saved ? Math.min(parseInt(saved, 10) || 0, totalSteps - 1) : 0;
    } catch { return 0; }
  });
  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    try {
      const saved = sessionStorage.getItem('cai_onboarding_answers');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [loading, setLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trialError, setTrialError] = useState<string | null>(null);

  // Animation direction: 'forward' | 'back'
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [animating, setAnimating] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Persist step progress to sessionStorage (survives remounts from ProtectedRoute)
  useEffect(() => {
    try {
      sessionStorage.setItem('cai_onboarding_step', String(currentStep));
    } catch { /* ignore */ }
  }, [currentStep]);

  useEffect(() => {
    try {
      sessionStorage.setItem('cai_onboarding_answers', JSON.stringify(answers));
    } catch { /* ignore */ }
  }, [answers]);

  // Ensure light mode
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  // Fire Meta Pixel Lead event once on mount (user just registered)
  // In trial flow, Lead fires only after successful card registration (handleTrialCardSubmit)
  const leadFired = useRef(false);
  useEffect(() => {
    if (!leadFired.current && !isTrialFlow && typeof fbq === 'function') {
      fbq('track', 'Lead');
      leadFired.current = true;
    }
  }, []);

  const isOnCardStep = isTrialFlow && currentStep === cardStepIndex;
  const step = isOnCardStep ? null : STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  const isSurveyLastStep = currentStep === STEPS.length - 1;
  const currentAnswer = step ? (answers[step.answerField] || '') : '';

  const handleSelect = (value: string) => {
    if (!step) return;
    setAnswers((prev) => ({ ...prev, [step.answerField]: value }));

    // Auto-advance to next question (except on the last survey step for non-trial, or card step)
    const shouldAutoAdvance = isTrialFlow
      ? currentStep < STEPS.length // auto-advance on all survey steps including last one → card step
      : !isSurveyLastStep; // normal: don't auto-advance on last survey step

    if (shouldAutoAdvance && !animating) {
      setTimeout(() => {
        animateTransition('forward', () => setCurrentStep((s) => s + 1));
      }, 400);
    }
  };

  const animateTransition = (dir: 'forward' | 'back', callback: () => void) => {
    setDirection(dir);
    setAnimating(true);
    setTimeout(() => {
      callback();
      setAnimating(false);
    }, 300);
  };

  const handleNext = () => {
    if (!currentAnswer) return;
    if (isLastStep) {
      handleFinalize();
      return;
    }
    animateTransition('forward', () => setCurrentStep((s) => s + 1));
  };

  const handleBack = () => {
    if (isFirstStep) return;
    animateTransition('back', () => setCurrentStep((s) => s - 1));
  };

  // Trial card step submission
  const handleTrialCardSubmit = async (cardData: TrialCardFormData) => {
    if (blockInSupportMode()) return;
    setTrialError(null);
    setLoading(true);
    try {
      // 1. Save onboarding answers first
      await apiPost('/api/onboarding/save', { answers, flow: flow || undefined });

      // 2. Register card and start trial
      await apiPost('/api/subscriptions/trial', { payment: cardData });

      // 2.5. Fire Meta Pixel Lead event after successful card registration (trial v2 flow)
      if (typeof fbq === 'function') {
        fbq('track', 'Lead');
      }

      // 3. Clear session progress
      try {
        sessionStorage.removeItem('cai_onboarding_step');
        sessionStorage.removeItem('cai_onboarding_answers');
        sessionStorage.removeItem('cai_onboarding_flow');
      } catch { /* ignore */ }

      // 4. Show loading screen and redirect to dashboard
      setShowLoading(true);
      await refreshStatus();
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1500);
    } catch (err: any) {
      setTrialError(err?.message || t('onboardingSurvey.trialStep.cardValidationFailed'));
      setLoading(false);
    }
  };

  const handleFinalize = async () => {
    setError(null);
    setLoading(true);
    try {
      await apiPost('/api/onboarding/save', { answers, flow: flow || undefined });
      // Clear session progress now that save succeeded
      try {
        sessionStorage.removeItem('cai_onboarding_step');
        sessionStorage.removeItem('cai_onboarding_answers');
        sessionStorage.removeItem('cai_onboarding_flow');
      } catch { /* ignore */ }
      // Show loading screen
      setShowLoading(true);
      // Refresh user status so ProtectedRoute sees onboardingCompleted=true
      await refreshStatus();

      // Flow-based redirect: "demo" flow sends high-revenue users to booking page
      const HIGH_REVENUE = ['10k_50k', '50k_200k', 'acima_200k'];
      const shouldRedirectToDemo = flow === 'demo' && HIGH_REVENUE.includes(answers.faturamento || '');

      // Wait 1.5s then redirect
      setTimeout(() => {
        if (shouldRedirectToDemo) {
          window.location.href = 'https://core.sprinthub.app/calendar/bZpahVFd30caQQw3jExjO';
        } else {
          navigate('/', { replace: true });
        }
      }, 1500);
    } catch (err: any) {
      setError(err?.message || t('onboardingSurvey.errors.saveFailed'));
      setLoading(false);
    }
  };

  // Loading screen (post-onboarding)
  if (showLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
          <p className="text-lg font-medium text-foreground">{t('onboardingSurvey.loading.title')}</p>
          <p className="text-sm text-muted-foreground">{t('onboardingSurvey.loading.subtitle')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Column - Onboarding Content */}
      <div className="w-full lg:w-[40%] flex flex-col px-6 sm:px-12 lg:px-16 py-8 bg-white overflow-y-auto">
        <div className="max-w-md mx-auto w-full flex flex-col flex-1">
          {/* Logo */}
          <CoreStudioLogo width={140} className="text-foreground mb-8" />

          {/* Progress Indicator */}
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm font-medium text-muted-foreground">
              {t('onboardingSurvey.step', { current: currentStep + 1, total: totalSteps })}
            </span>
            <div className="flex-1 flex gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                    i <= currentStep ? 'bg-primary' : 'bg-border'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 relative overflow-hidden">
            {isOnCardStep ? (
              /* Trial Card Registration Step */
              <TrialCardStep
                onSubmit={handleTrialCardSubmit}
                loading={loading}
                error={trialError}
              />
            ) : (
              /* Survey Question + Options (animated) */
              <div
                ref={contentRef}
                className={`transition-all duration-300 ease-in-out ${
                  animating
                    ? direction === 'forward'
                      ? 'opacity-0 -translate-x-4'
                      : 'opacity-0 translate-x-4'
                    : 'opacity-100 translate-x-0'
                }`}
              >
                {/* Question */}
                <h2 className="text-xl font-bold text-foreground mb-6 leading-tight">
                  {step && t(step.questionKey)}
                </h2>

                {/* Options Grid */}
                {step && (
                  <div className={`grid gap-3 ${
                    step.columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'
                  }`}>
                    {step.options.map((option) => (
                      <SelectableCard
                        key={option.value}
                        label={t(option.labelKey)}
                        selected={currentAnswer === option.value}
                        onClick={() => handleSelect(option.value)}
                        disabled={loading}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Error (survey steps only) */}
          {error && !isOnCardStep && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mt-4">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Navigation Footer (hidden on card step — it has its own submit) */}
          {!isOnCardStep && (
            <div className="flex items-center justify-between pt-6 mt-4 border-t border-border">
              {!isFirstStep ? (
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={loading || animating}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t('onboardingSurvey.back')}
                </button>
              ) : (
                <div />
              )}

              <Button
                onClick={handleNext}
                disabled={!currentAnswer || loading || animating}
                className="min-w-[140px]"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isLastStep ? (
                  t('onboardingSurvey.finalize')
                ) : (
                  <>
                    {t('onboardingSurvey.next')}
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Back button on card step */}
          {isOnCardStep && (
            <div className="pt-4 mt-2">
              <button
                type="button"
                onClick={handleBack}
                disabled={loading}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('onboardingSurvey.back')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right Column - Branding (hidden on mobile) */}
      <div
        className="hidden lg:flex lg:w-[60%] items-center justify-center p-12"
        style={{ background: 'linear-gradient(135deg, #030513 0%, #0a1628 40%, #0f2027 70%, #030513 100%)' }}
      >
        <div className="text-center space-y-8 max-w-lg">
          <div className="flex justify-center">
            <div
              className="p-6 rounded-3xl"
              style={{
                background: 'linear-gradient(135deg, rgba(71,99,202,0.15) 0%, rgba(71,99,202,0.05) 100%)',
                border: '1px solid rgba(71,99,202,0.2)',
              }}
            >
              <CoreStudioMark size={80} />
            </div>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
            {t('onboardingSurvey.brandingTitle')}
          </h2>
          <p className="text-gray-400 text-lg">
            {t('onboardingSurvey.brandingSubtitle')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
